import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import TopBar from "@/components/TopBar";
import TwilioDeviceProvider from "@/components/TwilioDeviceProvider";
import { useCallNotifications } from "@/hooks/useCallNotifications";
import { useVideoRingNotificationsContext } from "@/contexts/VideoRingNotificationsContext";

import { useActivityTracker } from "@/hooks/useActivityTracker";
import { DashboardAccessNotificationProvider } from "@/components/dashboard/DashboardAccessNotificationProvider";
import { VideoRingProvider, useVideoRing } from "@/contexts/VideoRingContext";
import { VideoRingProgressModal } from "@/components/calls/VideoRingProgressModal";
import { DailyRingProvider, useDailyRing } from "@/contexts/DailyRingContext";
import { DailyRingProgressModal } from "@/components/calls/DailyRingProgressModal";
import { MobileBottomNav } from "@/components/navigation/MobileBottomNav";
import { ConnectionConfirmationModal } from "@/components/calls/ConnectionConfirmationModal";
import { DailyVideoCallModal } from "@/components/calls/DailyVideoCallModal";
import { MobileUIProvider, useMobileUI } from "@/contexts/MobileUIContext";
import { PerformanceProvider } from "@/contexts/PerformanceContext";
import { ImpersonationBanner } from "@/components/admin/ImpersonationBanner";
import { cn } from "@/lib/utils";

function LayoutContent({ children, currentPageName }) {
  const location = useLocation();
  const clearInteractionLocks = () => {
    const root = document.getElementById('root');
    const main = document.querySelector('[data-app-main]');

    document.documentElement.style.pointerEvents = '';
    document.body.style.pointerEvents = '';
    if (root) root.style.pointerEvents = '';
    if (main instanceof HTMLElement) main.style.pointerEvents = '';

    document.documentElement.removeAttribute('data-scroll-locked');
    document.body.removeAttribute('data-scroll-locked');
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.paddingRight = '';
  };

  // Enable call notifications globally
  useCallNotifications();

  // Video ring notifications (includes activeDailyCall for consultant)
  const { activeDailyCall, clearActiveDailyCall, isProcessing } = useVideoRingNotificationsContext();
  
  // Enable activity tracking for usage metrics
  useActivityTracker();

  const { videoRingData, closeModal } = useVideoRing();
  const { dailyRingData, closeModal: closeDailyModal } = useDailyRing();
  
  // Mobile UI state - hide navigation when in chat view
  const { hideNavigation } = useMobileUI();

  // Failsafe: clear any residual pointer-events / scroll locks on route change
  // This prevents Radix UI components (Select, Popover, etc.) from leaving
  // interaction locks that block sidebar navigation
  // Mobile: aggressive cleanup on route change (Radix + iOS/PWA quirks)
  useEffect(() => {
    if (window.innerWidth >= 768) return;

    clearInteractionLocks();

    const strayOverlays = document.querySelectorAll('[data-radix-popper-content-wrapper]');
    strayOverlays.forEach(overlay => {
      if (overlay.parentElement === document.body) {
        overlay.remove();
      }
    });

    const t1 = window.setTimeout(clearInteractionLocks, 50);
    const t2 = window.setTimeout(clearInteractionLocks, 180);
    const t3 = window.setTimeout(clearInteractionLocks, 360);
    const t4 = window.setTimeout(clearInteractionLocks, 700);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      window.clearTimeout(t4);
    };
  }, [location.pathname]);

  // Desktop: MutationObserver to clean orphaned data-scroll-locked from Radix
  useEffect(() => {
    if (window.innerWidth < 768) return;

    const observer = new MutationObserver(() => {
      if (!document.body.hasAttribute('data-scroll-locked')) return;

      setTimeout(() => {
        const hasOpenOverlay = document.querySelector(
          '[data-radix-select-content], [role="dialog"][data-state="open"], ' +
          '[data-radix-popper-content-wrapper], [data-state="open"][role="listbox"]'
        );
        if (!hasOpenOverlay && document.body.hasAttribute('data-scroll-locked')) {
          clearInteractionLocks();
        }
      }, 400);
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-scroll-locked']
    });

    return () => observer.disconnect();
  }, []);

  return (
    <DashboardAccessNotificationProvider>
      <SidebarProvider defaultOpen={true} style={{ "--sidebar-width-icon": "4.5rem" }}>
        <div className="flex h-dvh w-full bg-background overflow-hidden">
          <AppSidebar />
          <div className="flex-1 flex flex-col w-full min-w-0 min-h-0 overflow-hidden">
              <ImpersonationBanner />
            {/* TopBar - hidden on mobile when in chat view */}
            <div className={cn(hideNavigation && "hidden md:block")}>
              <TopBar currentPageName={currentPageName} />
            </div>
            <main data-app-main className={cn(
              "flex-1 flex flex-col min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain bg-slate-50 dark:bg-slate-900 touch-pan-y",
              "[-webkit-overflow-scrolling:touch]",
              hideNavigation ? "pb-0" : "pb-[var(--bottom-nav-total-height)] md:pb-0"
            )}>
              {children}
            </main>
          </div>
          
          {/* Gerenciador global do Twilio Device e chamadas entrantes */}
          <TwilioDeviceProvider />
        </div>
        
        {/* Mobile Bottom Navigation - hidden when in chat view */}
        {!hideNavigation && <MobileBottomNav />}
      </SidebarProvider>

      {/* Video Ring Progress Modal - renderizado no Layout para evitar re-renders */}
      {videoRingData && (
        <VideoRingProgressModal
          ringRequestId={videoRingData.ring_request_id}
          initialLevel={videoRingData.current_level}
          initialTimeout={videoRingData.timeout_seconds}
          initialUsers={videoRingData.available_users}
          onClose={closeModal}
        />
      )}

      {/* Daily Ring Progress Modal - sistema separado do Video NOW */}
      {dailyRingData && (
        <DailyRingProgressModal
          ringRequestId={dailyRingData.ring_request_id}
          initialLevel={dailyRingData.current_level}
          initialTimeout={dailyRingData.timeout_seconds}
          initialUsers={dailyRingData.available_users}
          onClose={closeDailyModal}
        />
      )}

      {/* Video Connection Confirmation Modal - suppressed during active Daily call */}
      <ConnectionConfirmationModal suppressWhenDailyActive={!!activeDailyCall || isProcessing} />

      {/* Daily Video Call - persistent floating modal */}
      {activeDailyCall && (
        <DailyVideoCallModal
          activeDailyCall={activeDailyCall}
          onClose={clearActiveDailyCall}
        />
      )}
    </DashboardAccessNotificationProvider>
  );
}

export default function Layout({ children, currentPageName }) {
  return (
    <VideoRingProvider>
      <DailyRingProvider>
        <MobileUIProvider>
          <PerformanceProvider>
            <LayoutContent children={children} currentPageName={currentPageName} />
          </PerformanceProvider>
        </MobileUIProvider>
      </DailyRingProvider>
    </VideoRingProvider>
  );
}
